import { ObserversModule } from '@angular/cdk/observers';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { mergeDeep } from '@netwealth/nw-utils';
import { IconComponent } from '../icon/icon.component';
import { SanitizeHtmlPipe } from '../umbraco-blocks/pipes/sanitize-html.pipe';

export type ExpansionPanelConfig = {
    open?: boolean;
    shadow?: boolean;
    tabIndex?: number;
    chevron?: {
        align?: 'start' | 'end' | 'center';
        color?: string;
        vertical?: boolean;
    };
    backgroundColor?: string;
    disableAnimations?: boolean;
    hasBorder?: boolean;
};

@Component({
    selector: 'nw-expansion-panel',
    templateUrl: './expansion-panel.component.html',
    styleUrls: ['./expansion-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ObserversModule, IconComponent, SanitizeHtmlPipe]
})
export class ExpansionPanelComponent implements OnChanges {
    private readonly DEFAULT_CONFIG: ExpansionPanelConfig = {
        open: false,
        shadow: true,
        tabIndex: undefined,
        chevron: {
            align: 'center',
            vertical: false
        },
        disableAnimations: false,
        hasBorder: false
    };

    @Input() config: ExpansionPanelConfig = { ...this.DEFAULT_CONFIG };
    @Input() header?: string;
    @Input() body?: string;
    @Output() raiseEvent: EventEmitter<Event> = new EventEmitter<Event>();
    @Output() isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('content') content!: ElementRef;
    @ViewChild('contentBody') contentBody!: ElementRef;

    @HostListener('window:resize') windowResize(): void {
        this._resize();
    }

    public configOpenChange($event: Event, open: boolean) {
        if (
            ($event as KeyboardEvent)?.code === 'Space' &&
            $event.target === $event.currentTarget
        ) {
            $event.preventDefault();
        }

        this.raiseEvent.emit($event);

        if (
            // is a hyperlink
            ($event.target as Element)?.tagName?.toUpperCase() === 'A' ||
            // is a button
            ($event.target as Element)?.tagName?.toUpperCase() === 'BUTTON'
        ) {
            return;
        }

        this.config.open = open;
        this.isOpenChange.emit(this.config.open);
    }

    public ngOnChanges({ config }: SimpleChanges): void {
        if (config?.currentValue) {
            this.config = mergeDeep(
                this.DEFAULT_CONFIG,
                this.config
            ) as ExpansionPanelConfig;
        }
    }

    public onContentChange(): void {
        this._resize();
    }

    private _resize(): void {
        if (!this.config.open) return;

        this.content.nativeElement.style.height =
            this.contentBody?.nativeElement?.clientHeight || 0;
    }
}
